<template>
  <paginated-table
    id="admin-users-table"
    ref="table"
    endpoint="users"
    :columns="columns"
    :extra-filters="extraFilters"
    selectable
    :label="$tc('users.model_name', 2)"
    sync-filters-with-url
    row-link
    :show-action-column="canArchiveUser || canImpersonateUser"
    @row-selected="
      (users) => {
        selectedUsers = users;
        allUsersSelected = false;
      }
    "
    @filters-changed="filters = $event"
  >
    <template #head-buttons>
      <icon-button v-if="canCreateUser" role="add" to="/admin/users/new">
        {{ $t("users.list.create") | capitalize }}
      </icon-button>

      <b-button-group v-if="canResetUserPassword || canSendRegistrationEmails" variant="info">
        <b-dropdown
          left
          :disabled="selectedUsers.length === 0"
          :text="$t('views.admin.users.actions.grouped')"
        >
          <b-dropdown-item v-if="canResetUserPassword" @click="sendPasswordResetEmail">
            {{ $t("views.admin.users.actions.password_reset") }}
          </b-dropdown-item>
          <b-dropdown-item v-if="canSendRegistrationEmails" @click="sendRegistrationSubmittedEmail">
            {{ $t("views.admin.users.actions.welcome_completed") }}
          </b-dropdown-item>
          <b-dropdown-item v-if="canSendRegistrationEmails" @click="sendRegistrationApprovedEmail">
            {{ $t("views.admin.users.actions.welcome_approved") }}
          </b-dropdown-item>
          <b-dropdown-item v-if="canSendRegistrationEmails" @click="sendRegistrationRejectedEmail">
            {{ $t("views.admin.users.actions.registration_rejected") }}
          </b-dropdown-item>
          <b-dropdown-divider />
          <template v-if="communityRegionFilter">
            <b-dropdown-item @click="addAllToCommunity">
              {{ $t("views.admin.users.actions.add_to_community", { id: communityRegionFilter }) }}
            </b-dropdown-item>
            <b-dropdown-divider />
          </template>
          <b-dropdown-item disabled
            >{{ $tc("users.list.selected", selectedUserCount, { count: selectedUserCount }) }}
          </b-dropdown-item>
          <b-dropdown-item
            v-if="!allUsersSelected && $refs.table && $refs.table.total !== selectedUsers.length"
            @click="
              () => {
                $refs.table.selectAll();
                // Need to do this on the next tick, since the table.selectAll would undo it
                $nextTick(() => (allUsersSelected = true));
              }
            "
          >
            {{ $t("views.admin.users.actions.select_all") }} ({{
              $refs.table && $refs.table.total
            }})
          </b-dropdown-item>
        </b-dropdown>
      </b-button-group>
    </template>

    <template #cell(is_inactive)="{ item }">
      <b-badge :variant="item.is_inactive ? 'danger' : 'success'">
        {{
          item.is_inactive
            ? $t("users.enums.is_inactive.inactive")
            : $t("users.enums.is_inactive.active")
        }}
      </b-badge>
    </template>

    <template #cell(actions)="{ item }">
      <div class="user-actions">
        <admin-list-actions
          v-if="!item.data_deleted_at"
          :item="item"
          :item-name="item.full_name"
          slug="users"
          :show-destroy="canArchiveUser"
          :show-restore="canArchiveUser"
          :restore-params="restoreParams"
          :archive-label="$t('users.archive')"
          :restore-label="$t('users.restore')"
          archive-icon="person-x"
          @change="$refs.table.refresh"
        >
          <template #destroy-question>
            <div>
              <strong>{{ $t("views.admin.users.archive.title", { name: item.full_name }) }}</strong>
              <i18n path="views.admin.users.archive.warning" tag="p">
                <template #name>
                  {{ item.full_name }}
                </template>
                <template #warning_borrower>
                  <router-link
                    target="_blank"
                    :to="`/admin/loans?borrower_user.id=${item.id}&status=in_process`"
                    >{{ $t("views.admin.users.archive.warning_borrower") }}</router-link
                  >
                </template>
                <template #warning_owner>
                  <router-link
                    target="_blank"
                    :to="`/admin/loans?loanable.owner_user_id=${item.id}&status=in_process`"
                    >{{ $t("views.admin.users.archive.warning_owner") }}</router-link
                  >
                </template>
              </i18n>
            </div>
          </template>
          <template #restore-question>
            <div>
              <strong>{{
                $t("views.admin.users.archive.restore_title", { name: item.full_name })
              }}</strong>
              <b-form-checkbox v-model="restoreParams.restore_loanables" class="mt-3">
                {{ $t("views.admin.users.archive.restore_loanables") }}
              </b-form-checkbox>
              <b-form-checkbox
                v-model="restoreParams.restore_availability"
                :disabled="!restoreParams.restore_loanables"
                class="mt-3"
              >
                {{ $t("views.admin.users.archive.restore_availability") }}
              </b-form-checkbox>
            </div>
          </template>
          <b-dropdown-item
            v-if="!item.deleted_at && canImpersonateUser"
            :id="'mandate-' + item.id"
            @click="mandate(item.id)"
          >
            <b-icon icon="person-badge" variant="warning" />
            {{ $t("views.admin.users.impersonate") }}
          </b-dropdown-item>
          <validated-button
            v-if="item.deleted_at"
            variant="danger"
            type="b-dropdown-item"
            icon="x-octagon-fill"
            :action="
              async function () {
                await fullyDelete(item.id);
              }
            "
            :label="$t('views.admin.users.delete.label')"
            :question="
              $t('views.admin.users.delete.title', { name: item.full_name }) +
              ' ' +
              $t('views.admin.users.delete.warning')
            "
          />
        </admin-list-actions>
      </div>
    </template>
  </paginated-table>
</template>

<script>
import AdminListActions from "@/components/Admin/ListActions.vue";
import IconButton from "@/components/shared/IconButton.vue";
import PaginatedTable from "@/components/shared/PaginatedTable.vue";
import { Column, Filter } from "@/components/shared/PaginatedTableColumns";
import ValidatedButton from "@/components/shared/ValidatedButton.vue";
import { capitalize } from "@/helpers/filters";
import { getCommunityAdminFilter } from "@/helpers/permissions/communities";
import {
  canManageGlobalRoles,
  canSeeDeletedUser,
  canViewBankDetails,
  isGlobalAdmin,
} from "@/helpers/permissions/users";
import { del, put } from "@/requests/server";

export default {
  name: "AdminUsers",
  components: {
    IconButton,
    ValidatedButton,
    PaginatedTable,
    AdminListActions,
  },
  data() {
    return {
      allUsersSelected: false,
      selectedUsers: [],
      filters: {},
      restoreParams: {
        restore_loanables: false,
        restore_availability: false,
      },
    };
  },
  computed: {
    extraFilters() {
      const extraFilters = [
        new Filter(
          "communities.id",
          capitalize(this.$t("communities.fields.community.name")),
          "relation",
          {
            relation: "communities",
            label: "name",
            field: "id",
            params: { ...getCommunityAdminFilter(this.$store.state.user, "read", "id") },
          }
        ),
        new Filter(
          "community_user_tag",
          capitalize(this.$t("views.admin.users.filters.community_user_tag.title")),
          "select",
          [
            {
              value: null,
              label: capitalize(
                this.$t("views.admin.users.filters.community_user_tag.options.all")
              ),
            },
            {
              value: "volunteer",
              label: capitalize(
                this.$t("views.admin.users.filters.community_user_tag.options.volunteer")
              ),
            },
          ]
        ),
        new Filter(
          "is_in_community_region",
          capitalize(this.$t("views.admin.users.filters.in_region")),
          "relation",
          {
            relation: "communities",
            label: "name",
            field: "id",
            params: {
              ...getCommunityAdminFilter(this.$store.state.user, "read", "id"),
              type: "borough",
            },
          }
        ),
      ];

      if (canSeeDeletedUser(this.$store.state.user)) {
        extraFilters.push(
          new Filter(
            "is_deleted",
            capitalize(this.$t("views.admin.users.filters.disabled")),
            "boolean"
          )
        );
      }

      if (canManageGlobalRoles(this.$store.state.user)) {
        extraFilters.push(
          new Filter(
            "user_roles.role",
            capitalize(this.$t("user_roles.fields.role.title")),
            "select",
            [
              { value: null, label: this.$t("user_roles.fields.role.options.null.any") },
              { value: "accountant", label: this.$t("user_roles.fields.role.options.accountant") },
              {
                value: "car_sharing_manager",
                label: this.$t("user_roles.fields.role.options.car_sharing_manager"),
              },
              { value: "admin", label: this.$t("user_roles.fields.role.options.admin") },
            ]
          )
        );
        extraFilters.push(
          new Filter(
            "community_admins",
            capitalize(this.$t("views.admin.users.filters.community_admin")),
            "boolean"
          )
        );
      }

      return extraFilters;
    },
    columns() {
      const columns = [
        new Column("id", capitalize(this.$t("lists.id")), "id"),
        new Column("address", capitalize(this.$t("users.fields.address")), "text", {
          showByDefault: false,
        }),
        new Column(
          "created_at",
          capitalize(this.$t("views.admin.users.columns.created_at")),
          "date",
          {
            showByDefault: false,
          }
        ),
        new Column("full_name", capitalize(this.$t("users.fields.full_name")), "text", {
          urlFct: (item) => !item.deleted_at && `/admin/users/${item.id}`,
        }),
        new Column("email", capitalize(this.$t("users.fields.email"))),
        new Column("phone", capitalize(this.$t("users.fields.phone"))),
        new Column("is_inactive", capitalize(this.$t("users.fields.is_inactive")), "text"),
        new Column("last_login_at", capitalize(this.$t("users.fields.last_login_at")), "date", {
          showByDefault: false,
        }),
      ];

      if (canViewBankDetails(this.$store.state.user)) {
        columns.push(
          new Column(
            "bank_transit_number",
            capitalize(this.$t("users.fields.bank_transit_number")),
            "number",
            {
              showByDefault: false,
            }
          )
        );
        columns.push(
          new Column(
            "bank_institution_number",
            capitalize(this.$t("users.fields.bank_institution_number")),
            "number",
            {
              showByDefault: false,
            }
          )
        );
        columns.push(
          new Column(
            "bank_account_number",
            capitalize(this.$t("users.fields.bank_account_number")),
            "number",
            {
              showByDefault: false,
            }
          )
        );
      }

      return columns;
    },
    canCreateUser() {
      // Global admins only for the moment.
      const loggedInUser = this.$store.state.user;
      return isGlobalAdmin(loggedInUser);
    },
    canArchiveUser() {
      // Global admins only for the moment.
      const loggedInUser = this.$store.state.user;
      return isGlobalAdmin(loggedInUser);
    },
    canEditUser() {
      // Global admins only for the moment.
      const loggedInUser = this.$store.state.user;
      return isGlobalAdmin(loggedInUser);
    },
    canImpersonateUser() {
      // Global admins only for the moment.
      const loggedInUser = this.$store.state.user;
      return isGlobalAdmin(loggedInUser);
    },
    canResetUserPassword() {
      // Global admins only for the moment.
      const loggedInUser = this.$store.state.user;
      return isGlobalAdmin(loggedInUser);
    },
    canSendRegistrationEmails() {
      // Global admins only for the moment.
      const loggedInUser = this.$store.state.user;
      return isGlobalAdmin(loggedInUser);
    },
    communityRegionFilter() {
      return this.filters.is_in_community_region;
    },
    selectedUserCount() {
      return this.allUsersSelected ? this.$refs.table.total : this.selectedUsers.length;
    },
  },
  methods: {
    async mandate(mandatedUserId) {
      this.$store.dispatch("account/mandate", { mandatedUserId });
    },
    async fullyDelete(userId) {
      await del(`/users/${userId}`, {
        axiosRequestConfig: { data: { delete_data: true } },
        notifications: {
          action: this.$t("views.admin.users.notifications.delete_data.action"),
          onSuccess: this.$t("views.admin.users.notifications.delete_data.success"),
        },
      });
      this.$refs.table.refresh();
    },
    getMassActionParams() {
      if (this.allUsersSelected) {
        return {
          ...this.$refs.table.fetchParams,
          ...this.$refs.table.filters,
          per_page: "*",
        };
      }
      return {
        id: this.selectedUsers.map((s) => s.id).join(","),
      };
    },

    async addAllToCommunity() {
      await put(
        `/users/add_to_community/${this.communityRegionFilter}`,
        this.getMassActionParams(),
        {
          notifications: {
            action: this.$t("views.admin.users.notifications.add_to_community.action"),
          },
        }
      );

      this.$store.commit("addNotification", {
        content: this.$t("views.admin.users.notifications.add_to_community.message", {
          count: this.selectedUserCount,
        }),
        title: this.$t("views.admin.users.notifications.add_to_community.success"),
        variant: "success",
      });

      this.$refs.table.refresh();
    },
    displayMailStatus(data) {
      const { report } = data;

      if (report) {
        const succeeded = report.reduce(
          (acc, s) => acc + (s.response.status === "success" ? 1 : 0),
          0
        );
        const failed = report.reduce((acc, s) => acc + (s.response.status === "error" ? 1 : 0), 0);
        this.$store.commit("addNotification", {
          content: this.$t("views.admin.users.notifications.send_email.message", {
            succeeded,
            failed,
          }),
          title: this.$t("views.admin.users.notifications.send_email.title"),
          variant: "success",
          type: "send_email",
        });
      }
    },
    async sendPasswordResetEmail() {
      const { data } = await put(`/users/send/password_reset`, null, {
        axiosRequestConfig: {
          params: this.getMassActionParams(),
        },
        notifications: {
          action: this.$t("views.admin.users.notifications.send_email.action"),
        },
      });
      this.displayMailStatus(data);
    },
    async sendRegistrationSubmittedEmail() {
      const { data } = await put(`/users/send/registration_submitted`, null, {
        axiosRequestConfig: {
          params: this.getMassActionParams(),
        },
        notifications: {
          action: this.$t("views.admin.users.notifications.send_email.action"),
        },
      });
      this.displayMailStatus(data);
    },
    async sendRegistrationApprovedEmail() {
      const { data } = await put(`/users/send/registration_approved`, null, {
        axiosRequestConfig: {
          params: this.getMassActionParams(),
        },
        notifications: {
          action: this.$t("views.admin.users.notifications.send_email.action"),
        },
      });
      this.displayMailStatus(data);
    },
    async sendRegistrationRejectedEmail() {
      const { data } = await put(`/users/send/registration_rejected`, null, {
        axiosRequestConfig: {
          params: this.getMassActionParams(),
        },
        notifications: {
          action: this.$t("views.admin.users.notifications.send_email.action"),
        },
      });
      this.displayMailStatus(data);
    },
  },
};
</script>

<style lang="scss">
.user-actions {
  display: flex;
}
</style>
